/* import __COLOCATED_TEMPLATE__ from './export-schedule-frequency-item.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { indexBy } from 'underscore';
import {
  DayPosition,
  SchedulePreset,
  Step,
} from 'embercom/models/reporting/dataset-export-schedule';
import { isEmpty } from '@ember/utils';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import { tracked } from '@glimmer/tracking';
// @ts-ignore
import { copy } from 'ember-copy';

interface Args {
  text: string;
  schedule: DatasetExportSchedule;
  onSelect: () => void;
  isSelected: boolean;
}

export default class ExportScheduleFrequencyItem extends Component<Args> {
  @service declare intl: any;
  @service declare appService: AppService;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;

  declare exportRuleSnapshot: DatasetExportSchedule['exportRule'];
  @tracked declare localExportRule: DatasetExportSchedule['exportRule'];

  get selectedCadence() {
    return this.localExportRule.interval.step;
  }

  get timezone() {
    return this.args.schedule.timezone;
  }

  get frequencies() {
    let basePath = 'reporting.data-export.schedule-export.new-schedule.custom-frequency';
    return [
      {
        text: this.intl.t(`${basePath}.weeks`),
        value: Step.WEEKLY,
      },
      {
        text: this.intl.t(`${basePath}.months`),
        value: Step.MONTHLY,
      },
    ];
  }

  get daysOfWeek() {
    let days = [
      { text: this.intl.t('app.lib.reporting.week-days.sunday'), value: 0 },
      { text: this.intl.t('app.lib.reporting.week-days.monday'), value: 1 },
      { text: this.intl.t('app.lib.reporting.week-days.tuesday'), value: 2 },
      { text: this.intl.t('app.lib.reporting.week-days.wednesday'), value: 3 },
      { text: this.intl.t('app.lib.reporting.week-days.thursday'), value: 4 },
      { text: this.intl.t('app.lib.reporting.week-days.friday'), value: 5 },
      { text: this.intl.t('app.lib.reporting.week-days.saturday'), value: 6 },
    ];
    return days.map((day) => ({
      ...day,
      isSelected: this.localExportRule.daysOfWeek.includes(day.value),
    }));
  }

  @action
  onDaysSelectionChange(value: number[]) {
    if (value.length > 0) {
      this.localExportRule = {
        ...this.localExportRule,
        daysOfWeek: value,
      };
    }
  }

  @action
  onStepSelectionChange(value: string) {
    let oldExportRule = this.localExportRule;
    let dayPosition = oldExportRule.interval.dayPosition;
    if (value === Step.MONTHLY) {
      // when user selects monthly, we can only select one day of the week.
      // day postion is defaulted to last if none is selected
      this.localExportRule = {
        ...oldExportRule,
        daysOfWeek: [this.firstWeekDayOrWeekend],
        interval: {
          ...oldExportRule.interval,
          step: value,
          dayPosition: dayPosition ?? DayPosition.LAST,
        },
      };
    } else {
      this.localExportRule = {
        ...oldExportRule,
        interval: {
          ...oldExportRule.interval,
          step: value,
          dayPosition: undefined,
        },
      };
    }
  }

  get firstWeekDay() {
    return this.localExportRule.daysOfWeek.find((day) => day > 0 && day < 6);
  }

  get firstWeekDayOrWeekend() {
    return this.firstWeekDay ?? this.localExportRule.daysOfWeek[0];
  }

  get isMonthlyStep() {
    return this.localExportRule.interval.step === Step.MONTHLY;
  }

  get selectedDaysLabel() {
    let indexedDays = indexBy(this.daysOfWeek, 'value');
    let label = this.localExportRule.daysOfWeek.map((day) => indexedDays[day].text);
    if (label.length > 2) {
      return `${label.slice(0, 4).join(', ')}...`;
    }
    return label.join(', ');
  }

  @action
  updateIntervalValue(e: Event) {
    let value = (e.target as HTMLInputElement).value;
    if (!isNaN(Number(value))) {
      this.localExportRule = {
        ...this.localExportRule,
        interval: {
          ...this.localExportRule.interval,
          value: Number(value),
        },
      };
    }
  }

  get applyButtonDisabled() {
    return isEmpty(this.localExportRule.interval.value);
  }

  snapshotExportRule() {
    this.exportRuleSnapshot = this.args.schedule.exportRule;
  }

  setDefaultCustomPreset() {
    let baseRule = this.datasetExportScheduleService.exportRuleFrom(
      SchedulePreset.EVERY_MONDAY,
      this.timezone,
    );
    this.localExportRule = {
      ...baseRule,
      deliveryTime: { ...this.args.schedule.exportRule.deliveryTime },
    };
  }

  @action
  onShow() {
    this.snapshotExportRule();
    if (this.args.schedule.isPreset) {
      this.setDefaultCustomPreset();
    } else {
      this.localExportRule = copy(this.args.schedule.exportRule, true);
    }
  }

  @action
  onApply() {
    this.args.schedule.exportRule = this.localExportRule;
    this.args.onSelect();
  }

  @action
  onCancel() {
    this.args.schedule.exportRule = this.exportRuleSnapshot;
  }

  @action
  onDayPositionChange(value: DayPosition[]) {
    this.localExportRule = {
      ...this.localExportRule,
      interval: {
        ...this.localExportRule.interval,
        dayPosition: value[0],
      },
    };
  }

  get selectedDayPositionLabel() {
    let indexedDayPositionItems = indexBy(this.dayPositionItems, 'value');
    if (this.localExportRule.interval.dayPosition) {
      return indexedDayPositionItems[this.localExportRule.interval.dayPosition].text;
    }
    return indexedDayPositionItems[DayPosition.FIRST].text;
  }

  get dayPositionItems() {
    let items = [
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.first'),
        value: 'first',
      },
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.second'),
        value: 'second',
      },
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.third'),
        value: 'third',
      },
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.last'),
        value: 'last',
      },
    ];
    return items.map((item) => ({
      ...item,
      isSelected: this.localExportRule.interval.dayPosition === item.value,
    }));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ExportScheduleFrequencyItem': typeof ExportScheduleFrequencyItem;
    'reporting/custom/export-schedule-frequency-item': typeof ExportScheduleFrequencyItem;
  }
}
